<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import { useTopBarsStore } from '@/stores/top-bars'
  import { useUserStore } from '@/stores/user'
  import { useMenuStore } from '@/stores/menu'
  import { track } from '@/helpers/mixpanelDes'
  import type { TTopBarContent, TTopBarVariant } from '@/types/top-bars'
  import DesTopBar from '@/components/bar/DesTopBar.vue'

  const props = defineProps<{
    alertOnly?: boolean
  }>()

  const topBars = useTopBarsStore()
  const users = useUserStore()
  const menu = useMenuStore()

  const alertClosed = ref<boolean>(false)

  const paymentAlert: TTopBarContent = {
    id: 'payment',
    message: 'There is an issue with your payment.',
    badge: 'IMPORTANT',
    link_label: 'Please click to learn more',
    link_url: 'https://learn.designrr.io/en/articles/7863887-update-payment-information',
    ends_on: null,
  }

  const alertVisible = computed<boolean>(
    () => users.user?.account_status === 'REBILL_FAIL_DEACTIVATION' && !alertClosed.value,
  )

  const promoVisible = computed<boolean>(
    () =>
      !props.alertOnly &&
      !!topBars.promoBar &&
      topBars.canShow('promo', topBars.promoBar.id, topBars.promoBar.max_views),
  )

  const featureVisible = computed<boolean>(
    () =>
      !props.alertOnly &&
      !!topBars.featureBar &&
      topBars.canShow('feature', topBars.featureBar.id, topBars.featureBar.max_views),
  )

  const current = computed<{ variant: TTopBarVariant; bar: TTopBarContent } | null>(() => {
    if (alertVisible.value) {
      return { variant: 'alert', bar: paymentAlert }
    }
    if (promoVisible.value && topBars.promoBar) {
      return { variant: 'promo', bar: topBars.promoBar }
    }
    if (featureVisible.value && topBars.featureBar) {
      return { variant: 'feature', bar: topBars.featureBar }
    }

    return null
  })

  watch(
    current,
    (value) => {
      if (value && value.variant !== 'alert') {
        topBars.registerView(value.variant, value.bar.id)
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    if (props.alertOnly) {
      return
    }

    topBars.fetchActiveBars()
  })

  function close(): void {
    if (!current.value) {
      return
    }

    const { variant, bar } = current.value
    if (variant === 'alert') {
      alertClosed.value = true
    } else {
      topBars.dismiss(variant, bar.id)
    }

    track('top-bar-dismissed', { variant, id: bar.id })
  }

  function action(): void {
    if (!current.value) {
      return
    }

    const { variant, bar } = current.value

    track('top-bar-clicked', { variant, id: bar.id, url: bar.link_url })
  }
</script>

<template>
  <Transition leave-to-class="opacity-0 -translate-y-2">
    <div v-if="current" class="transition-all" :class="{ 'md:ml-navigation': menu.isOpen }">
      <div class="mx-3 mt-3">
        <DesTopBar
          :variant="current.variant"
          :message="current.bar.message"
          :badge="current.bar.badge"
          :ends-on="current.bar.ends_on"
          :link-label="current.bar.link_label"
          :link-url="current.bar.link_url"
          @close="close"
          @action="action"
        />
      </div>
    </div>
  </Transition>
</template>
